import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { DateRangeFilter } from '@/components/reports/DateRangeFilter';
import { ExportButton } from '@/components/reports/ExportButton';
import { FunnelChart } from '@/components/reports/FunnelChart';
import { ScoreHistogram } from '@/components/reports/ScoreHistogram';
import { MetricCard } from '@/components/reports/MetricCard';
import { CandidateReportTable } from '@/components/reports/CandidateReportTable';
import { useJobReport } from '@/hooks/useReports';

function getDefaultDates() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
}

export default function JobReportPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const { data, isLoading, error } = useJobReport(jobId!, startDate, endDate);

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/reports')} className="text-sm text-blue-600 hover:underline">← Relatórios</button>
            <h1 className="text-xl font-semibold text-gray-900">{data?.title || 'Relatório da Vaga'}</h1>
          </div>
          <div className="flex items-center gap-3">
            <DateRangeFilter startDate={startDate} endDate={endDate} onChange={(s, e) => { setStartDate(s); setEndDate(e); }} />
            <ExportButton reportType="job" startDate={startDate} endDate={endDate} jobId={jobId} />
          </div>
        </div>

        {isLoading && <p className="text-gray-500">Carregando relatório...</p>}
        {error && <p className="text-red-600">Erro ao carregar: {error.message}</p>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <MetricCard label="Fit Score Médio" value={data.fitScoreStats.average.toFixed(1)} />
              <MetricCard label="Fit Score Mediana" value={data.fitScoreStats.median.toFixed(1)} />
              <MetricCard label="Tempo Médio Triagem" value={data.averageScreeningTime.toFixed(1)} suffix="horas" />
              <MetricCard label="Total Candidatos" value={data.candidates.length} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <FunnelChart data={data.funnel} />
              <ScoreHistogram data={data.fitScoreDistribution} title="Distribuição Fit Score" />
            </div>

            {data.interviewScoreDistribution && data.interviewScoreStats && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <ScoreHistogram data={data.interviewScoreDistribution} title="Distribuição Interview Score" color="#a78bfa" />
                <div className="grid grid-cols-2 gap-4">
                  <MetricCard label="Interview Score Médio" value={data.interviewScoreStats.average.toFixed(1)} />
                  <MetricCard label="Interview Score Mediana" value={data.interviewScoreStats.median.toFixed(1)} />
                  {data.finalScoreStats && (
                    <>
                      <MetricCard label="Final Score Médio" value={data.finalScoreStats.average.toFixed(1)} />
                      <MetricCard label="Final Score Mediana" value={data.finalScoreStats.median.toFixed(1)} />
                    </>
                  )}
                </div>
              </div>
            )}

            <CandidateReportTable candidates={data.candidates} />
          </>
        )}
      </div>
    </AppLayout>
  );
}
